/**
 * Date formatting utility
 * Provides consistent date formatting across the application
 */

/**
 * Formats a date string or Date object to MM/DD/YYYY HH:MM format
 * @param date - The date to format (string or Date object)
 * @param includeTime - Whether to include time (default: true)
 * @returns Formatted date string
 */
export const formatDate = (date: string | Date | undefined | null, includeTime: boolean = true): string => {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid Date';

    const dateStr = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    if (includeTime) {
      const timeStr = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${dateStr} ${timeStr}`;
    }

    return dateStr;
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Formats a date to relative time (e.g., "2 days ago")
 * @param date - The date to format
 * @returns Relative time string
 */
export const formatRelativeDate = (date: string | Date | undefined | null): string => {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid Date';

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  } catch (error) {
    return 'Invalid Date';
  }
};
